import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;
const RATE_LIMIT_ERROR_MESSAGE = 'Too many attempts. Please wait a moment and try again.';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  /** @type {boolean} */
  #isSubmitting = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.#syncInitialFormState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    // Prevent duplicate in-flight submissions that can trigger cart rate limits.
    if (this.#isSubmitting) return;

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const currentVariantId = this.refs.variantId?.value;
      const quantity = this.#getQuantity();

      // Queue only when the picker is actually transitioning to a different variant.
      if (intendedVariantId && currentVariantId && intendedVariantId !== currentVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
        this.refs.addToCartButtonContainer?.animateAddToCart?.();
        return;
      }

      // Recover from stale "variant change in progress" state and submit normally.
      this.#variantChangeInProgress = false;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    const checkedVariantInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return (
      checkedVariantInput?.dataset.variantId ||
      this.refs.variantId?.value ||
      undefined
    );
  }

  /** @returns {number} */
  #getQuantity() {
    const quantityFromSelector = Number(this.refs.quantitySelector?.getValue?.());
    if (Number.isFinite(quantityFromSelector) && quantityFromSelector > 0) {
      return quantityFromSelector;
    }

    const quantityInput = /** @type {HTMLInputElement | null} */ (this.querySelector('form input[name="quantity"]'));
    const quantityFromInput = Number(quantityInput?.value);
    if (Number.isFinite(quantityFromInput) && quantityFromInput > 0) {
      return quantityFromInput;
    }

    const defaultQuantity = Number(this.dataset.quantityDefault) || 1;
    return defaultQuantity > 0 ? defaultQuantity : 1;
  }

  /** @returns {boolean} */
  #isCurrentVariantSoldOut() {
    if (!this.refs.variantId?.value || this.refs.variantId?.disabled) {
      return true;
    }

    const unavailableSelectedOption = this.querySelector(
      'input[type="radio"]:checked[data-option-available="false"]'
    );
    return Boolean(unavailableSelectedOption);
  }

  #syncInitialFormState() {
    const form = this.querySelector('form');
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (form) {
      const quantityInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));
      if (quantityInput) {
        quantityInput.value = this.#normalizeQuantity(quantityInput.value || this.dataset.quantityDefault || '1').toString();
      }
    }

    if (this.#isCurrentVariantSoldOut()) {
      this.#setAddToCartSoldOutState(true);
      return;
    }

    const hasValidVariantSelected = Boolean(this.refs.variantId?.value);
    for (const container of allAddToCartContainers) {
      if (hasValidVariantSelected) {
        container.enable();
      } else {
        container.disable();
      }
    }
    this.#setAddToCartSoldOutState(false);
  }

  /**
   * @param {number | string | FormDataEntryValue | null | undefined} quantity
   * @returns {number}
   */
  #normalizeQuantity(quantity) {
    const parsedQuantity = Number(quantity);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity < 1) {
      return Math.max(1, Math.floor(this.#getQuantity()));
    }

    return Math.max(1, Math.floor(parsedQuantity));
  }

  /**
   * Validates add-to-cart payload before requesting Shopify cart API.
   * @param {FormData} formData
   * @returns {string | undefined}
   */
  #getAddToCartValidationError(formData) {
    const variantId = (formData.get('id') || '').toString().trim();
    const quantity = Number(formData.get('quantity') || this.dataset.quantityDefault || 1);

    if (this.refs.variantId?.disabled) {
      return 'This variant is sold out.';
    }

    const unavailableSelectedOption = this.querySelector(
      'input[type="radio"]:checked[data-option-available="false"]'
    );
    if (unavailableSelectedOption) {
      return 'This variant is sold out.';
    }

    if (!/^\d+$/.test(variantId)) {
      return 'Please select a valid product option.';
    }

    if (!Number.isFinite(quantity) || quantity < 1) {
      return 'Please enter a valid quantity.';
    }

    return undefined;
  }

  /**
   * Finds a fallback available variant id from visible variant inputs.
   * @param {string} currentVariantId
   * @returns {string | undefined}
   */
  #findAlternativeVariantId(currentVariantId) {
    const variantInputs = this.querySelectorAll('input[type="radio"][data-variant-id][data-option-available="true"]');
    for (const input of variantInputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      const variantId = input.dataset.variantId;
      if (variantId && variantId !== currentVariantId) {
        return variantId;
      }
    }

    return undefined;
  }

  /**
   * Fetches product JSON and returns a valid available variant id.
   * @param {string} currentVariantId
   * @returns {Promise<string | undefined>}
   */
  async #findAvailableVariantIdFromProduct(currentVariantId) {
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return undefined;

    const normalizedPath = productUrl.split('?')[0] || productUrl;
    const productJsonUrl = normalizedPath.endsWith('.js') ? normalizedPath : `${normalizedPath}.js`;

    try {
      const response = await fetch(productJsonUrl, {
        headers: {
          Accept: 'application/json',
        },
      });
      if (!response.ok) return undefined;

      const product = await response.json();
      const fallbackVariant = product?.variants?.find(
        (variant) => Boolean(variant?.available) && String(variant.id) !== currentVariantId
      );

      return fallbackVariant?.id ? String(fallbackVariant.id) : undefined;
    } catch (_) {
      return undefined;
    }
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   * @param {boolean} [didRetryOnSoldOut]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event, didRetryOnSoldOut = false) {
    const { addToCartTextError } = this.refs;
    let handoffToRetry = false;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    if (this.#isSubmitting) return;
    this.#isSubmitting = true;
    for (const container of allAddToCartContainers) {
      container.disable();
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    // Ensure variant id is always present even if the hidden input was disabled.
    if (!formData.get('id')) {
      const fallbackVariantId = overrideVariantId || this.refs.variantId?.value;
      if (fallbackVariantId) {
        formData.set('id', fallbackVariantId);
      }
    }

    const normalizedVariantId = (formData.get('id') || '').toString().trim();
    if (normalizedVariantId) {
      formData.set('id', normalizedVariantId);
    }

    const normalizedQuantity = this.#normalizeQuantity(overrideQuantity ?? formData.get('quantity'));
    formData.set('quantity', normalizedQuantity.toString());

    const quantityInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));
    if (quantityInput) {
      quantityInput.value = normalizedQuantity.toString();
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    if (cartItemComponentsSectionIds.length > 0) {
      formData.set('sections', cartItemComponentsSectionIds.join(','));
    }

    const payloadValidationError = this.#getAddToCartValidationError(formData);
    if (payloadValidationError) {
      this.#isSubmitting = false;
      this.#showAddToCartError(payloadValidationError);

      if (/sold out/i.test(payloadValidationError)) {
        this.refs.variantId.disabled = true;
        this.#setAddToCartSoldOutState(true);
      }

      const hasValidVariantSelected = Boolean(this.refs.variantId?.value) && !this.refs.variantId?.disabled;
      for (const container of allAddToCartContainers) {
        if (hasValidVariantSelected) {
          container.enable();
        } else {
          container.disable();
        }
      }
      return;
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    })
      .then(async (httpResponse) => {
        /** @type {any} */
        let response;

        try {
          response = await httpResponse.json();
        } catch (_) {
          response = {
            status: httpResponse.status || 500,
            message:
              httpResponse.status === 429
                ? RATE_LIMIT_ERROR_MESSAGE
                : `Add to cart failed (${httpResponse.status || 'unknown'})`,
            description: '',
            errors: {},
          };
        }

        return { httpResponse, response };
      })
      .then(async ({ httpResponse, response }) => {
        const hasError = !httpResponse.ok || Boolean(response?.status);
        if (hasError) {
          const errorMessage = response?.description || response?.message || RATE_LIMIT_ERROR_MESSAGE;
          const currentVariantId = (formData.get('id') || '').toString();

          // Stale variant ids can return sold-out 422; retry once with an alternative available variant.
          if (!didRetryOnSoldOut && httpResponse.status === 422 && /sold out/i.test(errorMessage)) {
            let fallbackVariantId = this.#findAlternativeVariantId(currentVariantId);
            if (!fallbackVariantId) {
              fallbackVariantId = await this.#findAvailableVariantIdFromProduct(currentVariantId);
            }
            const fallbackQuantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
            if (fallbackVariantId) {
              this.refs.variantId.value = fallbackVariantId;
              this.refs.variantId.disabled = false;
              handoffToRetry = true;
              this.#isSubmitting = false;
              this.#processAddToCart(fallbackVariantId, fallbackQuantity, event, true);
              return;
            }
          }

          if (/sold out/i.test(errorMessage)) {
            this.refs.variantId.disabled = true;
            this.#setAddToCartSoldOutState(true);
          }

          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', errorMessage, response?.description, response?.errors)
          );

          this.#showAddToCartError(errorMessage);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
        this.#showAddToCartError(RATE_LIMIT_ERROR_MESSAGE);
      })
      .finally(() => {
        if (handoffToRetry) return;

        this.#isSubmitting = false;
        const isSoldOut = this.refs.variantId?.disabled === true;
        if (isSoldOut) {
          this.#setAddToCartSoldOutState(true);
        } else {
          const hasValidVariantSelected = Boolean(this.refs.variantId?.value);
          for (const container of allAddToCartContainers) {
            if (hasValidVariantSelected) {
              container.enable();
            } else {
              container.disable();
            }
          }
          this.#setAddToCartSoldOutState(false);
        }

        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    if (this.#isSubmitting) {
      this.#addToCartQueue.unshift(...items);
      return;
    }
    this.#isSubmitting = true;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );
    for (const container of allAddToCartContainers) {
      container.disable();
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(async (httpResponse) => {
        /** @type {any} */
        let response;
        try {
          response = await httpResponse.json();
        } catch (_) {
          response = {
            status: httpResponse.status || 500,
            message:
              httpResponse.status === 429
                ? RATE_LIMIT_ERROR_MESSAGE
                : `Add to cart failed (${httpResponse.status || 'unknown'})`,
            description: '',
            errors: {},
          };
        }

        return { httpResponse, response };
      })
      .then(async ({ httpResponse, response }) => {
        const hasError = !httpResponse.ok || Boolean(response?.status);
        if (hasError) {
          const errorMessage = response?.description || response?.message || RATE_LIMIT_ERROR_MESSAGE;

          if (/sold out/i.test(errorMessage)) {
            this.refs.variantId.disabled = true;
            this.#setAddToCartSoldOutState(true);
          }

          this.dispatchEvent(
            new CartErrorEvent(this.id, errorMessage, response?.description, response?.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = errorMessage;
            } else {
              addToCartTextError.appendChild(document.createTextNode(errorMessage));
            }
            this.#setLiveRegionText(errorMessage);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        await this.#fetchAndUpdateCartQuantity();

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
        this.#showAddToCartError(RATE_LIMIT_ERROR_MESSAGE);
      })
      .finally(() => {
        this.#isSubmitting = false;
        const isSoldOut = this.refs.variantId?.disabled === true;
        if (isSoldOut) {
          this.#setAddToCartSoldOutState(true);
          return;
        }

        const hasValidVariantSelected = Boolean(this.refs.variantId?.value);
        for (const container of allAddToCartContainers) {
          if (hasValidVariantSelected) {
            container.enable();
          } else {
            container.disable();
          }
        }
        this.#setAddToCartSoldOutState(false);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /** @returns {string} */
  #getSoldOutText() {
    return this.dataset.soldOutText || 'Sold out';
  }

  /**
   * @param {HTMLButtonElement | undefined} button
   * @returns {HTMLElement | null}
   */
  #getAddToCartButtonTextNode(button) {
    if (!(button instanceof HTMLButtonElement)) return null;

    const nestedTextNode = button.querySelector('.add-to-cart-text__content > span > span');
    if (nestedTextNode instanceof HTMLElement) {
      return nestedTextNode;
    }

    const fallbackTextNode = button.querySelector('.add-to-cart-text__content');
    return fallbackTextNode instanceof HTMLElement ? fallbackTextNode : null;
  }

  /**
   * @param {HTMLButtonElement | undefined} button
   * @param {string | undefined} text
   */
  #setAddToCartButtonText(button, text) {
    const textNode = this.#getAddToCartButtonTextNode(button);
    if (!textNode || !text) return;

    if (!button.dataset.defaultAddToCartText) {
      button.dataset.defaultAddToCartText = textNode.textContent?.trim() || '';
    }

    textNode.textContent = text;
  }

  /**
   * @param {boolean} isSoldOut
   */
  #setAddToCartSoldOutState(isSoldOut) {
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    for (const container of allAddToCartContainers) {
      const button = container.refs.addToCartButton;

      if (isSoldOut) {
        container.disable();
        this.#setAddToCartButtonText(button, this.#getSoldOutText());
        continue;
      }

      const defaultText = button?.dataset.defaultAddToCartText;
      if (defaultText) {
        this.#setAddToCartButtonText(button, defaultText);
      }
    }
  }

  /**
   * @param {string} message
   */
  #showAddToCartError(message) {
    const { addToCartTextError } = this.refs;
    if (!addToCartTextError) return;

    addToCartTextError.classList.remove('hidden');

    const textNode = addToCartTextError.childNodes[2];
    if (textNode) {
      textNode.textContent = message;
    } else {
      addToCartTextError.appendChild(document.createTextNode(message));
    }

    this.#setLiveRegionText(message);

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      addToCartTextError.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';
    variantId.disabled = !event.detail.resource?.id || event.detail.resource.available === false;

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    this.#setAddToCartSoldOutState(event.detail.resource == null || event.detail.resource.available == false);

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    } else if (newQuantityInput) {
      const quantityInput = /** @type {HTMLInputElement | null} */ (this.querySelector('form input[name="quantity"]'));
      if (quantityInput) {
        const quantityValue = newQuantityInput.value || newQuantityInput.min || this.dataset.quantityDefault || '1';
        quantityInput.value = this.#normalizeQuantity(quantityValue).toString();
      }
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
